import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'
import api from '../../services/apiService'
import MemberParticipation from '../../components/client-admin/MemberParticipation'
import { MaleIcon, FemaleIcon } from '../../components/common/GenderIcon'

// Format a registration timestamp (Y-m-d H:i:s) as a readable date/year.
const formatRegisterDate = (d) => {
    if (!d) return 'N/A'
    const date = new Date(String(d).replace(' ', 'T'))
    if (isNaN(date.getTime())) return d
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const PlayerManagement = () => {
    const { tenant, siteSettings } = useTenant()
    const navigate = useNavigate()
    const [currentView, setCurrentView] = useState('stateWise') // 'stateWise', 'playerList', 'playerDetail'
    const [stateWiseData, setStateWiseData] = useState([])
    const [playersList, setPlayersList] = useState([])
    const [playerDetail, setPlayerDetail] = useState(null)
    const [selectedState, setSelectedState] = useState('')
    const [activeCategory, setActiveCategory] = useState('Player') // 'Coach', 'Manager', or 'Player'
    const [activeGender, setActiveGender] = useState('Male') // 'Male' or 'Female' - Only for Player category
    // Tenants organise by state or by city; cities are stored as districts.
    // A tenant can pick its default with the unit_grouping setting.
    const [groupBy, setGroupBy] = useState(siteSettings?.unit_grouping || 'state')
    const unitLabel = groupBy === 'city' ? 'City' : 'State'
    const [showBlocked, setShowBlocked] = useState(false)
    const [blockedMembers, setBlockedMembers] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [isEditing, setIsEditing] = useState(false)
    const [editedPlayer, setEditedPlayer] = useState(null)
    const [participation, setParticipation] = useState(null)
    const [loadingParticipation, setLoadingParticipation] = useState(false)

    // Load state-wise player count on component mount and when category/gender changes
    useEffect(() => {
        loadStateWiseData()
    }, [activeCategory, activeGender])

    // Handle keyboard shortcuts (ESC to go back)
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'Escape') {
                if (currentView === 'playerDetail') {
                    handleBackToPlayerList()
                } else if (currentView === 'playerList') {
                    handleBackToStateWise()
                } else if (currentView === 'stateWise') {
                    handleBackToDashboard()
                }
            }
        }

        document.addEventListener('keydown', handleKeyPress)
        return () => document.removeEventListener('keydown', handleKeyPress)
    }, [currentView, groupBy])

    const loadStateWiseData = async () => {
        setLoading(true)
        setError('')
        try {
            const params = { category: activeCategory, group_by: groupBy }
            // Only include gender filter for Player category
            if (activeCategory === 'Player') {
                params.gender = activeGender
            }

            const response = await api.get('/client-admin/players/state-wise-count', { params })
            if (response.data.success) {
                // Backend already returns filtered data with correct total
                // Just filter out states with 0 count
                const filteredData = response.data.data
                    .map(item => ({
                        state: item.state,
                        total: item.total
                    }))
                    .filter(item => item.total > 0)
                setStateWiseData(filteredData)
            } else {
                setError('Failed to load state-wise data')
            }
        } catch (err) {
            setError('Error loading state-wise data')
            console.error('Error:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleViewAllPlayers = async (state) => {
        setLoading(true)
        setError('')
        setSelectedState(state)

        try {
            const params = { state, category: activeCategory, group_by: groupBy }
            // Only include gender filter for Player category
            if (activeCategory === 'Player') {
                params.gender = activeGender
            }

            const response = await api.get('/client-admin/players/by-state', { params })
            if (response.data.success) {
                setPlayersList(response.data.data)
                setCurrentView('playerList')
            } else {
                setError('Failed to load players list')
            }
        } catch (err) {
            setError('Error loading players list')
            console.error('Error:', err)
        } finally {
            setLoading(false)
        }
    }

    // Load a member's tournament participation (dates/year + best medal for players)
    const loadParticipation = async (url) => {
        setParticipation(null)
        setLoadingParticipation(true)
        try {
            const res = await api.get(url)
            if (res.data.success) setParticipation(res.data.data)
        } catch (err) {
            console.error('Error loading participation:', err)
        } finally {
            setLoadingParticipation(false)
        }
    }

    const handleViewPlayerDetails = async (playerId) => {
        setLoading(true)
        setError('')

        try {
            const response = await api.get(`/client-admin/players/${playerId}`)
            if (response.data.success) {
                setPlayerDetail(response.data.data)
                setCurrentView('playerDetail')
                loadParticipation(`/client-admin/players/${playerId}/participation`)
            } else {
                setError('Failed to load player details')
            }
        } catch (err) {
            setError('Error loading player details')
            console.error('Error:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleBackToStateWise = () => {
        setCurrentView('stateWise')
        setPlayersList([])
        setSelectedState('')
    }

    const handleBackToPlayerList = () => {
        if (showBlocked) {
            setCurrentView('stateWise')
            setPlayerDetail(null)
            loadBlockedMembers()
        } else {
            setCurrentView('playerList')
            setPlayerDetail(null)
        }
    }

    const handleBackToDashboard = () => {
        navigate('/admin/dashboard')
    }

    const handleCategoryChange = (category) => {
        setActiveCategory(category)
        setShowBlocked(false)
        setCurrentView('stateWise')
        setPlayersList([])
        setSelectedState('')
        setPlayerDetail(null)
    }

    const handleGenderChange = (gender) => {
        setActiveGender(gender)
        setShowBlocked(false)
        setCurrentView('stateWise')
        setPlayersList([])
        setSelectedState('')
        setPlayerDetail(null)
    }

    const handleShowBlocked = () => {
        setShowBlocked(true)
        setCurrentView('stateWise')
        setPlayersList([])
        setSelectedState('')
        setPlayerDetail(null)
        loadBlockedMembers()
    }

    const loadBlockedMembers = async () => {
        setLoading(true)
        setError('')
        try {
            const response = await api.get('/client-admin/players/blocked')
            if (response.data.success) {
                setBlockedMembers(response.data.data || [])
            } else {
                setError('Failed to load blocked members')
            }
        } catch (err) {
            setError('Error loading blocked members')
            console.error('Error:', err)
        } finally {
            setLoading(false)
        }
    }

    // Player action handlers
    const handleImageUpload = () => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.onchange = (event) => {
            const file = event.target.files[0]
            if (file) {
                // Create preview URL
                const reader = new FileReader()
                reader.onload = (e) => {
                    const imageData = e.target.result
                    setPlayerDetail(prev => ({
                        ...prev,
                        image: imageData
                    }))
                    // Also update editedPlayer if in editing mode
                    if (isEditing) {
                        setEditedPlayer(prev => ({
                            ...prev,
                            image: imageData,
                            imageFile: file // Store the file for upload
                        }))
                    }
                }
                reader.readAsDataURL(file)
                // Here you would normally upload to server
                console.log('Image selected:', file.name)
            }
        }
        input.click()
    }

    const handleEditPlayer = async () => {
        if (isEditing) {
            // Save changes
            try {
                setLoading(true)

                const updateData = {
                    full_name: editedPlayer.fullName,
                    father_name: editedPlayer.fatherName,
                    date_of_birth: editedPlayer.dob,
                    gender: editedPlayer.gender === 'Male' ? 'M' : 'F',
                    state_name: editedPlayer.state,
                    contact: editedPlayer.contact,
                    email: editedPlayer.email,
                    position: editedPlayer.position,
                    blood_group: editedPlayer.bloodGroup,
                    height: editedPlayer.height,
                    weight: editedPlayer.weight,
                    address: editedPlayer.address,
                    pin_code: editedPlayer.pinCode,
                    district: editedPlayer.district,
                    emergency_contact: editedPlayer.emergencyContact,
                    aadhar_card: editedPlayer.aadharCard,
                    passport_number: editedPlayer.passportNum,
                    status: editedPlayer.status === 'Active' ? 'active' :
                           editedPlayer.status === 'Blocked' ? 'inactive' : 'pending'
                }

                console.log('Sending update data:', updateData)
                console.log('Player ID:', playerDetail.id)

                // If there's an image file, upload it separately
                if (editedPlayer.imageFile) {
                    const formData = new FormData()
                    formData.append('image', editedPlayer.imageFile)

                    try {
                        const imageResponse = await api.post(`/client-admin/players/${playerDetail.id}/upload-image`, formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        })
                        console.log('Image upload response:', imageResponse.data)

                        // Update player detail with new image URL
                        if (imageResponse.data.success && imageResponse.data.image_url) {
                            setPlayerDetail(prev => ({
                                ...prev,
                                image: imageResponse.data.image_url
                            }))
                            setEditedPlayer(prev => ({
                                ...prev,
                                image: imageResponse.data.image_url
                            }))
                        }
                    } catch (imageError) {
                        console.error('Image upload failed:', imageError)
                        alert('Image upload failed. Other changes will still be saved.')
                    }
                }

                const response = await api.put(`/client-admin/players/${playerDetail.id}`, updateData)

                if (response.data.success) {
                    setPlayerDetail(editedPlayer)
                    setIsEditing(false)
                    alert('Player details updated successfully!')
                } else {
                    throw new Error(response.data.message || 'Failed to update player')
                }
            } catch (error) {
                console.error('Error updating player:', error)
                console.error('Error response:', error.response?.data)
                const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
                alert(`Failed to update player details: ${errorMessage}`)
            } finally {
                setLoading(false)
            }
        } else {
            // Start editing
            setEditedPlayer({...playerDetail})
            setIsEditing(true)
        }
    }

    const handleDeletePlayer = async () => {
        if (window.confirm(`Are you sure you want to delete player ${playerDetail.fullName}? This action cannot be undone.`)) {
            try {
                setLoading(true)

                const response = await api.delete(`/client-admin/players/${playerDetail.id}`)

                if (response.data.success) {
                    alert('Player deleted successfully!')
                    handleBackToPlayerList()
                } else {
                    throw new Error(response.data.message || 'Failed to delete player')
                }
            } catch (error) {
                console.error('Error deleting player:', error)
                alert('Failed to delete player. Please try again.')
            } finally {
                setLoading(false)
            }
        }
    }

    const handleBlockPlayer = async () => {
        const isBlocked = playerDetail.status === 'Inactive'
        const action = isBlocked ? 'unblock' : 'block'

        if (window.confirm(`Are you sure you want to ${action} player ${playerDetail.fullName}?`)) {
            try {
                setLoading(true)

                const updateData = {
                    status: isBlocked ? 'active' : 'inactive'
                }

                console.log('Updating player status:', updateData)
                const response = await api.patch(`/client-admin/players/${playerDetail.id}/status`, updateData)

                if (response.data.success) {
                    setPlayerDetail(prev => ({
                        ...prev,
                        status: isBlocked ? 'Active' : 'Inactive'
                    }))

                    // Send notification to user when player is blocked
                    if (!isBlocked && playerDetail.user_detail_id) {
                        try {
                            await api.post('/client-admin/notifications/send', {
                                title: 'Player Blocked',
                                message: `Your player ${playerDetail.fullName} (${playerDetail.playerRegId}) has been blocked by the admin.`,
                                type: 'player_blocked',
                                user_ids: [playerDetail.user_detail_id]
                            })
                        } catch (notifError) {
                            console.error('Error sending notification:', notifError)
                            // Don't show error to admin if notification fails
                        }
                    }

                    alert(`Player ${action}ed successfully!`)
                } else {
                    throw new Error(response.data.message || `Failed to ${action} player`)
                }
            } catch (error) {
                console.error(`Error ${action}ing player:`, error)
                console.error('Error response:', error.response?.data)
                const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
                alert(`Failed to ${action} player: ${errorMessage}`)
            } finally {
                setLoading(false)
            }
        }
    }

    const handleCancelEdit = () => {
        setIsEditing(false)
        setEditedPlayer(null)
    }

    const handleExportPlayers = async () => {
        try {
            setLoading(true)
            console.log('Starting export...')

            const response = await api.get('/client-admin/players/export', {
                responseType: 'blob' // Important for file download
            })

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'text/csv'
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url

            // Generate filename with current date
            const date = new Date().toISOString().split('T')[0]
            link.download = `players_data_${date}.csv`

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            console.log('Export completed successfully')

        } catch (error) {
            console.error('Export failed:', error)
            alert('Failed to export player data. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleFieldChange = (field, value) => {
        setEditedPlayer(prev => ({
            ...prev,
            [field]: value
        }))
    }

    // Category tabs component (Coach, Manager, Player)
    const CategoryTabs = () => (
        <div className="category-tabs">
            <button
                className={`tab-btn category-tab ${activeCategory === 'Coach' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('Coach')}
            >
                <i className="fas fa-chalkboard-teacher"></i> Coach
            </button>
            <button
                className={`tab-btn category-tab ${activeCategory === 'Manager' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('Manager')}
            >
                <i className="fas fa-user-tie"></i> Manager
            </button>
            <button
                className={`tab-btn category-tab ${activeCategory === 'Player' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('Player')}
            >
                <i className="fas fa-running"></i> Player
            </button>
        </div>
    )

    // Gender tabs component (only for Player category)
    const GenderTabs = () => (
        <div className="gender-tabs" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                    className={`tab-btn gender-tab ${!showBlocked && activeGender === 'Male' ? 'active' : ''}`}
                    onClick={() => handleGenderChange('Male')}
                >
                    <MaleIcon style={{ marginRight: '0.4rem' }} /> Male
                </button>
                <button
                    className={`tab-btn gender-tab ${!showBlocked && activeGender === 'Female' ? 'active' : ''}`}
                    onClick={() => handleGenderChange('Female')}
                >
                    <FemaleIcon style={{ marginRight: '0.4rem' }} /> Female
                </button>
            </div>
            <button
                className={`tab-btn gender-tab ${showBlocked ? 'active' : ''}`}
                onClick={handleShowBlocked}
                style={showBlocked ? { background: '#dc2626', color: '#fff', borderColor: '#dc2626' } : { color: '#dc2626', borderColor: '#fee2e2', background: '#fee2e2' }}
            >
                <i className="fas fa-ban"></i> Blocked List
            </button>
        </div>
    )

    // Blocked tab component (for Coach/Manager categories that don't have gender tabs)
    const BlockedTab = () => (
        <div className="gender-tabs" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
                className={`tab-btn gender-tab ${!showBlocked ? 'active' : ''}`}
                onClick={() => { setShowBlocked(false); loadStateWiseData() }}
                style={!showBlocked ? {} : { opacity: 0.7 }}
            >
                <i className="fas fa-list"></i> All
            </button>
            <button
                className={`tab-btn gender-tab ${showBlocked ? 'active' : ''}`}
                onClick={handleShowBlocked}
                style={showBlocked ? { background: '#dc2626', color: '#fff', borderColor: '#dc2626' } : { color: '#dc2626', borderColor: '#fee2e2', background: '#fee2e2' }}
            >
                <i className="fas fa-ban"></i> Blocked List
            </button>
        </div>
    )

    // Get display label based on category
    const getCategoryLabel = () => {
        if (activeCategory === 'Coach') return 'Coaches'
        if (activeCategory === 'Manager') return 'Managers'
        return activeGender === 'Male' ? 'Male Players' : 'Female Players'
    }

    // State-wise view component
    const StateWiseView = () => (
        <div className="player-management">
            <div className="page-header-banner">
                <div className="header-content">
                    <div className="header-icon">
                        <i className="fas fa-users"></i>
                    </div>
                    <div className="header-text">
                        <h1>{showBlocked ? 'Blocked Members' : `Players Data - ${unitLabel} wise`}</h1>
                        <p>{showBlocked ? 'All blocked players, coaches and managers' : `Complete ${unitLabel.toLowerCase()}-wise player statistics and management`}</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <button className="btn-export" onClick={handleExportPlayers}>
                        <i className="fas fa-download"></i> Export All Players Data
                    </button>
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        style={{
                            background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
                            border: 'none',
                            color: 'white',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 8px rgba(192, 15, 21, 0.4)'
                        }}
                    >
                        <i className="fas fa-arrow-left"></i> Back
                    </button>
                </div>
            </div>

            <CategoryTabs />
            {activeCategory === 'Player' ? <GenderTabs /> : <BlockedTab />}

            <div className="management-content">
                {loading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i> Loading...
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <i className="fas fa-exclamation-triangle"></i> {error}
                        <button onClick={showBlocked ? loadBlockedMembers : loadStateWiseData} className="retry-btn">
                            <i className="fas fa-redo"></i> Retry
                        </button>
                    </div>
                ) : showBlocked ? (
                    blockedMembers.length === 0 ? (
                        <div className="no-data-container">
                            <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                            <p>No blocked members found</p>
                        </div>
                    ) : (
                        <div className="players-list-table">
                            <p style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: '#6b7280' }}>
                                Total {blockedMembers.length} blocked member{blockedMembers.length > 1 ? 's' : ''}
                            </p>
                            <table>
                                <thead>
                                    <tr>
                                        <th>S.No.</th>
                                        <th>Reg. ID</th>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Gender</th>
                                        <th>State</th>
                                        <th>DOB</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blockedMembers.map((member, index) => (
                                        <tr key={member.id}>
                                            <td>{index + 1}</td>
                                            <td className="reg-id">{member.reg_id}</td>
                                            <td>{member.name}</td>
                                            <td>
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    background: member.type === 'Coach' ? '#dbeafe' : member.type === 'Manager' ? '#fef3c7' : '#f3e8ff',
                                                    color: member.type === 'Coach' ? '#1d4ed8' : member.type === 'Manager' ? '#92400e' : '#7c3aed',
                                                    padding: '0.2rem 0.5rem',
                                                    borderRadius: '4px',
                                                    fontSize: '0.7rem',
                                                    fontWeight: '600'
                                                }}>
                                                    {member.type}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`gender-badge ${member.gender.toLowerCase()}`}>
                                                    {member.gender}
                                                </span>
                                            </td>
                                            <td>{member.state}</td>
                                            <td>{member.dob ? new Date(member.dob).toLocaleDateString('en-IN') : '-'}</td>
                                            <td>
                                                <button
                                                    className="btn-view-details"
                                                    onClick={() => handleViewPlayerDetails(member.id)}
                                                >
                                                    <i className="fas fa-eye"></i> View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : stateWiseData.length === 0 ? (
                    <div className="no-data-container">
                        <i className="fas fa-info-circle"></i>
                        <p>No {getCategoryLabel()} found</p>
                    </div>
                ) : (
                    <div className="state-wise-table">
                        {/* Group the same records by state or by city. */}
                        <div className="grouping-toggle" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                            {['state', 'city'].map((mode) => (
                                <button
                                    key={mode}
                                    type="button"
                                    onClick={() => setGroupBy(mode)}
                                    aria-pressed={groupBy === mode}
                                    className={groupBy === mode ? 'btn-view-all' : ''}
                                    style={{
                                        padding: '6px 16px',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        border: '1px solid #d9d9d9',
                                        background: groupBy === mode ? undefined : '#fff',
                                        fontWeight: groupBy === mode ? 700 : 500,
                                    }}
                                >
                                    {mode === 'state' ? 'By State' : 'By City'}
                                </button>
                            ))}
                        </div>
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>{unitLabel} Name</th>
                                    <th>Total {activeCategory === 'Player' ? 'Players' : activeCategory + 's'}</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stateWiseData.map((stateData, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{stateData.state}</td>
                                        <td>{stateData.total}</td>
                                        <td>
                                            <button
                                                className="btn-view-all"
                                                onClick={() => handleViewAllPlayers(stateData.state)}
                                            >
                                                <i className="fas fa-eye"></i> View All
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )

    // Players list view component
    const PlayersListView = () => (
        <div className="player-management">
            <div className="page-header-banner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="header-content">
                    <div className="header-icon">
                        <i className="fas fa-users"></i>
                    </div>
                    <div className="header-text">
                        <h1>{activeCategory === 'Player'
                            ? `Players Data - ${selectedState} (${activeGender})`
                            : `${activeCategory}s Data - ${selectedState}`
                        }</h1>
                        <p>Total {playersList.length} {activeCategory === 'Player' ? 'players' : activeCategory.toLowerCase() + 's'} found</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button className="btn-back" onClick={handleBackToStateWise} title="Back to State-wise View"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#c00f15', color: '#fff', border: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                        <i className="fas fa-arrow-left"></i> Back
                    </button>
                </div>
            </div>

            <CategoryTabs />
            {activeCategory === 'Player' ? <GenderTabs /> : <BlockedTab />}

            <div className="management-content">
                {loading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i> Loading...
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <i className="fas fa-exclamation-triangle"></i> {error}
                    </div>
                ) : playersList.length === 0 ? (
                    <div className="no-data-container">
                        <i className="fas fa-info-circle"></i>
                        <p>No {activeCategory === 'Player' ? 'players' : activeCategory.toLowerCase() + 's'} found for {selectedState} {activeCategory === 'Player' ? `(${activeGender})` : ''}</p>
                    </div>
                ) : (
                    <div className="players-list-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Reg. ID</th>
                                    <th>{activeCategory === 'Player' ? 'Player' : activeCategory} Name</th>
                                    <th>Father Name</th>
                                    <th>Gender</th>
                                    <th>Position</th>
                                    <th>DOB</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {playersList.map((player, index) => (
                                    <tr key={player.id}>
                                        <td>{index + 1}</td>
                                        <td className="reg-id">{player.reg_id}</td>
                                        <td>{player.name}</td>
                                        <td>{player.father_name}</td>
                                        <td>
                                            <span className={`gender-badge ${player.gender.toLowerCase()}`}>
                                                {player.gender}
                                            </span>
                                        </td>
                                        <td>{player.position}</td>
                                        <td>{player.dob ? new Date(player.dob).toLocaleDateString('en-IN') : '-'}</td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                {player.status === 'inactive' && (
                                                    <span title="Blocked" style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '0.25rem',
                                                        background: '#fee2e2',
                                                        color: '#dc2626',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: '600'
                                                    }}>
                                                        <i className="fas fa-ban"></i> Blocked
                                                    </span>
                                                )}
                                                <button
                                                    className="btn-view-details"
                                                    onClick={() => handleViewPlayerDetails(player.id)}
                                                >
                                                    <i className="fas fa-eye"></i> View Details
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )

    // Player detail view component
    const PlayerDetailView = () => (
        <div className="player-management">
            <div className="page-header-banner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="header-content">
                    <div className="header-icon">
                        <i className="fas fa-id-card"></i>
                    </div>
                    <div className="header-text">
                        <h1>Player ID: {playerDetail?.regId || playerDetail?.id}</h1>
                        <p>Complete player information and details</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button className="btn-back" onClick={handleBackToPlayerList} title="Back to Players List"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#c00f15', color: '#fff', border: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                        <i className="fas fa-arrow-left"></i> Back
                    </button>
                </div>
            </div>

            <div className="management-content">
                {loading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin"></i> Loading...
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <i className="fas fa-exclamation-triangle"></i> {error}
                    </div>
                ) : playerDetail ? (
                    <div className="player-detail-form">
                        <div className="player-image-section">
                            <div className="image-placeholder">
                                {(isEditing && editedPlayer?.image) ? (
                                    <img src={editedPlayer.image} alt="Player" />
                                ) : playerDetail.image ? (
                                    <img src={playerDetail.image} alt="Player" />
                                ) : playerDetail.playerImage ? (
                                    <img src={playerDetail.playerImage} alt="Player" />
                                ) : (
                                    <i className="fas fa-user-circle"></i>
                                )}
                            </div>
                            <button className="btn-upload" onClick={handleImageUpload}>
                                <i className="fas fa-camera"></i> Choose Image
                            </button>
                        </div>

                        <div className="player-details-grid">
                            <div className="detail-field">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.fullName || '') : (playerDetail.fullName || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('fullName', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Father Name</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.fatherName || '') : (playerDetail.fatherName || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('fatherName', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>D.O.B</label>
                                <input
                                    type="date"
                                    value={isEditing ? (editedPlayer?.dob || '') : (playerDetail.dob || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('dob', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Gender</label>
                                {isEditing ? (
                                    <select
                                        value={editedPlayer?.gender || ''}
                                        onChange={(e) => handleFieldChange('gender', e.target.value)}
                                        className="editable"
                                    >
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                    </select>
                                ) : (
                                    <input type="text" value={playerDetail.gender || ''} disabled />
                                )}
                            </div>

                            <div className="detail-field">
                                <label>Blood Group</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.bloodGroup || '') : (playerDetail.bloodGroup || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('bloodGroup', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Height</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.height || '') : (playerDetail.height || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('height', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Weight</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.weight || '') : (playerDetail.weight || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('weight', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field full-width">
                                <label>Address</label>
                                <textarea
                                    value={isEditing ? (editedPlayer?.address || '') : (playerDetail.address || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('address', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>District</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.district || '') : (playerDetail.district || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('district', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>State</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.state || '') : (playerDetail.state || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('state', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Post code</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.pinCode || '') : (playerDetail.pinCode || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('pinCode', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Position</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.position || '') : (playerDetail.position || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('position', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Aadhar</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.aadharCard || '') : (playerDetail.aadharCard || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('aadharCard', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Passport</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.passportNum || '') : (playerDetail.passportNum || 'N/A')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('passportNum', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Contact</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.contact || '') : (playerDetail.contact || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('contact', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Emergency Contact</label>
                                <input
                                    type="text"
                                    value={isEditing ? (editedPlayer?.emergencyContact || '') : (playerDetail.emergencyContact || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('emergencyContact', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={isEditing ? (editedPlayer?.email || '') : (playerDetail.email || '')}
                                    disabled={!isEditing}
                                    onChange={(e) => handleFieldChange('email', e.target.value)}
                                    className={isEditing ? 'editable' : ''}
                                />
                            </div>

                            <div className="detail-field" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '14px', background: 'linear-gradient(135deg, #fff1f1 0%, #ffe4e6 100%)', border: '1px solid #f6b8bd', borderRadius: '14px', padding: '14px 18px', marginTop: '10px', boxShadow: '0 3px 12px rgba(192,15,21,0.12)' }}>
                                <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: 'linear-gradient(135deg, #e11d2a 0%, #c00f15 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 8px rgba(192,15,21,0.35)' }}>
                                    <i className="fas fa-calendar-check" style={{ color: '#fff', fontSize: '1.25rem' }}></i>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '0.7rem', letterSpacing: '0.09em', textTransform: 'uppercase', color: '#c00f15', fontWeight: 700 }}>Registered On</div>
                                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#7a0a0e', lineHeight: 1.25 }}>{formatRegisterDate(playerDetail.createdAt)}</div>
                                </div>
                            </div>
                        </div>

                        {!isEditing && activeCategory === 'Player' && (
                            <MemberParticipation data={participation} loading={loadingParticipation} />
                        )}

                        <div className="detail-actions">
                            {isEditing ? (
                                <>
                                    <button className="btn-save" onClick={handleEditPlayer}>
                                        <i className="fas fa-save"></i> SAVE CHANGES
                                    </button>
                                    <button className="btn-cancel" onClick={handleCancelEdit}>
                                        <i className="fas fa-times"></i> CANCEL
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        className={playerDetail.status === 'Inactive' ? 'btn-unblock' : 'btn-block'}
                                        onClick={handleBlockPlayer}
                                        style={{ width: 'auto', maxWidth: '220px', padding: '0.5rem 1.5rem', fontSize: '0.875rem' }}
                                    >
                                        <i className={playerDetail.status === 'Inactive' ? 'fas fa-unlock' : 'fas fa-ban'}></i>
                                        {playerDetail.status === 'Inactive' ? 'UNBLOCK PLAYER' : 'BLOCK PLAYER'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )

    // Render current view
    const renderCurrentView = () => {
        switch (currentView) {
            case 'stateWise':
                return <StateWiseView />
            case 'playerList':
                return <PlayersListView />
            case 'playerDetail':
                return <PlayerDetailView />
            default:
                return <StateWiseView />
        }
    }

    return renderCurrentView()
}

export default PlayerManagement
